import { debounce, throttle } from '@theme/utils'

const root = globalThis

const StickyHeaderConfig = {
  classes: {
    headerHidden: 'header-hidden',
    headerScrolled: 'scrolled',
    headerSticky: 'header-sticky',
    headerTransparent: 'header-transparent',
    isScrolled: 'is-scrolled',
    page404: 'page-404',
    pageIndex: 'page-index',
    scrollDown: 'scroll-down',
    scrollUp: 'scroll-up'
  },
  direction: {
    down: 'down',
    up: 'up'
  },
  thresholds: {
    directionThreshold: 5,
    hideThresholdRatio: 0.1,
    resizeDebounce: 150,
    scrollStopDebounce: 120,
    scrollThrottle: 50,
    stickyThresholdRatio: 0.2
  }
}

/**
 * @class StickyHeader
 * @extends HTMLElement
 * @description Cabeçalho fixo (sticky) que reage ao scroll da página.
 * Leituras de layout (innerHeight) são cacheadas e recalculadas apenas em resize
 * para evitar reflow forçado durante o scroll.
 */
export class StickyHeader extends HTMLElement {
  _cachedHideThreshold = 0
  _cachedStickyThreshold = 0
  _lastHeaderState = ''
  _lastScrollTop = 0
  _rafId = 0
  _scrollPending = false
  onResize
  onScroll
  onScrollStop
  scrollDirection = StickyHeaderConfig.direction.up

  constructor() {
    super()
    this.onScroll = throttle(
      this.onScrollHandler.bind(this),
      StickyHeaderConfig.thresholds.scrollThrottle
    )
    this.onScrollStop = debounce(
      this.onScrollStopHandler.bind(this),
      StickyHeaderConfig.thresholds.scrollStopDebounce
    )
    this.onResize = debounce(
      this.updateThresholds.bind(this),
      StickyHeaderConfig.thresholds.resizeDebounce
    )
  }

  connectedCallback() {
    this.init()

    root.addEventListener('scroll', this.onScroll, { passive: true })
    root.addEventListener('scroll', this.onScrollStop, { passive: true })
    root.addEventListener('resize', this.onResize, { passive: true })

    this._rafId = root.requestAnimationFrame(() => {
      this._rafId = 0
      this.updateThresholds()
      this._lastScrollTop = this.scrollTop
      this.onScrollHandler()
    })
  }

  disconnectedCallback() {
    root.removeEventListener('scroll', this.onScroll)
    root.removeEventListener('scroll', this.onScrollStop)
    root.removeEventListener('resize', this.onResize)
    if (this._rafId) root.cancelAnimationFrame(this._rafId)
  }

  init() {
    this.classList.toggle(
      StickyHeaderConfig.classes.headerTransparent,
      this.isTransparentPage
    )
  }

  /**
   * Recalcula os limites em cache. Chamado apenas em connect e resize,
   * evitando leituras de innerHeight em cada evento de scroll.
   */
  updateThresholds() {
    const vh = root.innerHeight || 0
    this._cachedStickyThreshold = Math.round(
      vh * StickyHeaderConfig.thresholds.stickyThresholdRatio
    )
    this._cachedHideThreshold = Math.round(
      vh * StickyHeaderConfig.thresholds.hideThresholdRatio
    )
  }

  get isTransparentPage() {
    const { classList } = document.body
    return (
      window.location.pathname === '/' ||
      classList.contains(StickyHeaderConfig.classes.pageIndex) ||
      classList.contains(StickyHeaderConfig.classes.page404)
    )
  }

  get scrollTop() {
    return Math.max(0, root.scrollY || 0)
  }

  /**
   * Handler de scroll otimizado: lê scrollY uma vez e adia todo o trabalho
   * de DOM para o próximo rAF. Coalesce múltiplos eventos em um único frame.
   */
  onScrollHandler() {
    if (this._scrollPending) return
    this._scrollPending = true

    this._rafId = root.requestAnimationFrame(() => {
      this._rafId = 0
      this._scrollPending = false
      const currentScrollTop = this.scrollTop
      this.updateScrollDirection(currentScrollTop)
      this.updateClasses(currentScrollTop)
      this._lastScrollTop = currentScrollTop
    })
  }

  updateScrollDirection(currentScrollTop) {
    if (currentScrollTop <= 0) {
      this.scrollDirection = StickyHeaderConfig.direction.up
      return
    }

    const scrollDifference = currentScrollTop - this._lastScrollTop
    const pastDirectionThreshold =
      Math.abs(scrollDifference) >=
      StickyHeaderConfig.thresholds.directionThreshold

    if (pastDirectionThreshold) {
      this.scrollDirection =
        scrollDifference < 0
          ? StickyHeaderConfig.direction.up
          : StickyHeaderConfig.direction.down
    }
  }

  updateClasses(currentScrollTop) {
    const pastSticky = currentScrollTop > this._cachedStickyThreshold
    const isDown = this.scrollDirection === StickyHeaderConfig.direction.down
    const shouldHide =
      pastSticky && currentScrollTop > this._cachedHideThreshold && isDown
    const headerState = `${pastSticky}:${shouldHide}:${isDown}`

    if (headerState === this._lastHeaderState) return

    this._lastHeaderState = headerState
    this.applyHeaderClasses(pastSticky, shouldHide)
    this.applyRootScrollClasses(isDown, pastSticky)
  }

  applyHeaderClasses(pastSticky, shouldHide) {
    const selfList = this.classList
    selfList.toggle(StickyHeaderConfig.classes.headerSticky, pastSticky)
    selfList.toggle(StickyHeaderConfig.classes.headerScrolled, pastSticky)
    selfList.toggle(StickyHeaderConfig.classes.headerHidden, shouldHide)
  }

  applyRootScrollClasses(isDown, pastSticky) {
    const rootList = document.documentElement.classList
    rootList.toggle(StickyHeaderConfig.classes.scrollUp, !isDown)
    rootList.toggle(StickyHeaderConfig.classes.scrollDown, isDown)
    rootList.toggle(StickyHeaderConfig.classes.isScrolled, pastSticky)
  }

  onScrollStopHandler() {
    root.requestAnimationFrame(() => {
      if (this.scrollTop > this._cachedStickyThreshold) return
      if (!this.classList.contains(StickyHeaderConfig.classes.headerScrolled)) {
        return
      }
      this.classList.remove(StickyHeaderConfig.classes.headerScrolled)
      this._lastHeaderState = ''
    })
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader)
}
